import { App, Form, Select, Tabs } from 'antd';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { batchUpdateCustomerConfig } from '@/services/ant-design-pro/api';
import ConfigExcelImportPanel from './ConfigExcelImportPanel';
import { CUSTOMER_CONFIG_EXCEL } from './excelCodes';
import {
  type BatchMode,
  type BatchScenarioPanelHandle,
  type BatchScenarioPanelProps,
  filterValidCustomers,
  SWITCH_OPTIONS,
} from './types';

const ValuationBatchPanel = forwardRef<
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps
>(({ customers, active, onDirty, onFooterChange }, ref) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [batchMode, setBatchMode] = useState<BatchMode>('uniform');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!active) return;
    form.setFieldsValue({
      forwardCalculationEnabled: true,
      factorReverseEnabled: false,
    });
    setBatchMode('uniform');
    setSubmitting(false);
  }, [active, form]);

  useEffect(() => {
    if (!active) return;
    onFooterChange({
      showOk: batchMode === 'uniform',
      submitting,
    });
  }, [active, batchMode, submitting, onFooterChange]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        if (batchMode !== 'uniform') {
          throw new Error('请在「统一修改」中确认，或使用 Excel / 场景规则页内的导入');
        }
        const values = await form.validateFields();
        const items = filterValidCustomers(customers).map((customer) => ({
          customerId: String(customer.id),
          forwardCalculationEnabled:
            values.forwardCalculationEnabled as boolean,
          factorReverseEnabled: values.factorReverseEnabled as boolean,
        }));

        if (items.length === 0) throw new Error('请选择客户');

        setSubmitting(true);
        const response = await batchUpdateCustomerConfig({ items });
        if (!response.success) {
          throw new Error(response.message || '批量编辑测算开关失败');
        }
        const result = response.result;
        if ((result?.successCount ?? 0) > 0) onDirty();
        if ((result?.failedCount ?? 0) > 0) {
          message.warning(
            `批量编辑完成：成功 ${result?.successCount ?? 0} 条，失败 ${result?.failedCount ?? 0} 条`,
          );
          return false;
        }
        message.success(`已成功修改 ${result?.successCount ?? 0} 个客户`);
        return true;
      } catch (error) {
        if (error && typeof error === 'object' && 'errorFields' in error) {
          return false;
        }
        message.error(
          error instanceof Error ? error.message : '批量编辑测算开关失败',
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
  }));

  return (
    <Tabs
      activeKey={batchMode}
      onChange={(key) => setBatchMode(key as BatchMode)}
      items={[
        {
          key: 'uniform',
          label: '统一修改',
          children: (
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                forwardCalculationEnabled: true,
                factorReverseEnabled: false,
              }}
            >
              <div style={{ marginBottom: 16, color: '#666' }}>
                将所选客户的默认测算开关统一修改为以下配置。
              </div>
              <Form.Item
                name="forwardCalculationEnabled"
                label="是否开启正向测算"
                rules={[{ required: true, message: '请选择是否开启正向测算' }]}
              >
                <Select placeholder="请选择" options={SWITCH_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="factorReverseEnabled"
                label="是否开启因子逆推"
                rules={[{ required: true, message: '请选择是否开启因子逆推' }]}
              >
                <Select placeholder="请选择" options={SWITCH_OPTIONS} />
              </Form.Item>
            </Form>
          ),
        },
        {
          key: 'excel',
          label: 'Excel批量修改',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.valuationConfig}
              description="下载模版填写后上传，将批量更新客户默认测算开关配置。"
              onSuccess={onDirty}
            />
          ),
        },
        {
          key: 'rules',
          label: '场景规则导入',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.valuationRule}
              description="下载场景规则模版填写后上传，将批量导入测算开关场景配置。"
              onSuccess={onDirty}
            />
          ),
        },
      ]}
    />
  );
});

ValuationBatchPanel.displayName = 'ValuationBatchPanel';

export default ValuationBatchPanel;
